import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getApiUrl } from "../config/api";

type Platform = "ozon" | "wildberries" | "yandex";

type Store = {
  id: string;
  platform: Platform;
  name: string;
  credentials: Record<string, any>;
  isActive: boolean;
};

type OzonListItem = {
  id: string;
  name: string;
  description?: string;
  images?: string[];
  price?: number;
  currency?: string;
  inStock?: boolean;
  offer_id?: string;
  product_id?: string;
};

// Raw types by platform
type YandexOfferMappingItem = {
  offer?: {
    offerId: string;
    name?: string;
    pictures?: string[];
    basicPrice?: { value?: number; currencyId?: string };
  };
};

const StoreProducts: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<OzonListItem[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const lastIdRef = useRef<string | undefined>(undefined);
  const navigate = useNavigate();
  const [searchProductId, setSearchProductId] = useState<string>("");
  const [appliedSearchId, setAppliedSearchId] = useState<string>("");
  const [ozonOfferId, setOzonOfferId] = useState<string>("");
  const [ozonProductId, setOzonProductId] = useState<string>("");
  const [appliedOzonOfferId, setAppliedOzonOfferId] = useState<string>("");
  const [appliedOzonProductId, setAppliedOzonProductId] = useState<string>("");
  const [yandexOfferId, setYandexOfferId] = useState<string>("");
  const [appliedYandexOfferId, setAppliedYandexOfferId] = useState<string>("");

  const authHeaders = useMemo(() => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    } as Record<string, string>;
  }, []);


  const filteredProducts = useMemo(() => {
    if (store?.platform === "wildberries" && appliedSearchId) {
      const query = appliedSearchId.toLowerCase();
      return products.filter((p) => String(p.id).toLowerCase().includes(query));
    }
    if (store?.platform === "ozon") {
      if (appliedOzonOfferId) {
        const q = appliedOzonOfferId.toLowerCase();
        return products.filter((p) =>
          String(p.offer_id || "")
            .toLowerCase()
            .includes(q)
        );
      }
      if (appliedOzonProductId) {
        const q = appliedOzonProductId.toLowerCase();
        return products.filter((p) =>
          String(p.product_id || p.id)
            .toLowerCase()
            .includes(q)
        );
      }
    }
    if (store?.platform === "yandex" && appliedYandexOfferId) {
      const q = appliedYandexOfferId.toLowerCase();
      return products.filter((p) => String(p.id).toLowerCase().includes(q));
    }
    return products;
  }, [
    products,
    appliedSearchId,
    store?.platform,
    appliedOzonOfferId,
    appliedOzonProductId,
    appliedYandexOfferId,
  ]);

  const loadStore = useCallback(async () => {
    try {
      const resp = await fetch(getApiUrl("/api/stores"), {
        headers: authHeaders,
      });
      if (!resp.ok) throw new Error("Не удалось загрузить магазины");
      const data = (await resp.json()) as Store[];
      const found = (data || []).find((s) => s.id === storeId);
      setStore(found || null);
    } catch (e: any) {
      setError(e?.message || "Ошибка загрузки магазина");
    }
  }, [authHeaders, storeId]);

  const loadProducts = useCallback(async () => {
    if (!store) return;
    setLoading(true);
    setError(null);
    try {
      if (store.platform === "ozon") {
        // Сырой список товаров OZON через общий endpoint
        const url = new URL(getApiUrl("/api/products/by-store"));
        url.searchParams.set("store", store.id);
        url.searchParams.set("limit", "20");
        if (lastIdRef.current)
          url.searchParams.set("lastId", lastIdRef.current);
        const resp = await fetch(url.toString(), { headers: authHeaders });
        if (!resp.ok)
          throw new Error("Не удалось получить список товаров OZON");
        const json = await resp.json();
        const items = (json?.items || []) as any[]; // raw ozon product info items
        setProducts((prev) => {
          const existing = new Map<string, OzonListItem>();
          for (const p of prev) existing.set(String(p.id), p);
          for (const it of items) {
            const productId = String(it?.product_id ?? it?.id ?? "");
            const offerId = String(
              it?.offer_id ?? it?.offerId ?? it?.sku ?? ""
            );
            const id = productId;
            if (!id || existing.has(id)) continue;
            const name = String(it?.name || `Товар ${id}`);
            const images: string[] =
              (it?.images as string[]) || (it?.primary_image as string[]) || [];
            existing.set(id, {
              id,
              name,
              images,
              price: it?.price,
              currency: it?.currency_code,
              offer_id: offerId || undefined,
              product_id: productId || undefined,
            });
          }
          return Array.from(existing.values());
        });
        const nextLastId =
          (json?.paging?.lastId as string | undefined) || undefined;
        lastIdRef.current = nextLastId;
        setHasMore(Boolean(json?.paging?.hasMore));
      } else if (store.platform === "wildberries") {
        // Сырой список карточек WB через общий endpoint
        const url = new URL(getApiUrl("/api/products/by-store"));
        url.searchParams.set("store", store.id);
        url.searchParams.set("limit", "20");
        // Для WB курсор — это объект, сериализуем в pageToken как JSON
        if (lastIdRef.current)
          url.searchParams.set("pageToken", lastIdRef.current);
        const resp = await fetch(url.toString(), { headers: authHeaders });
        if (!resp.ok) throw new Error("Не удалось получить список карточек WB");
        const json = await resp.json();
        const cards = (json?.items || []) as Array<any>;
        setProducts((prev) => {
          const existing = new Map<string, OzonListItem>();
          for (const p of prev) existing.set(String(p.id), p);
          for (const c of cards) {
            const id = String(
              c?.nmID ?? c?.nmId ?? c?.nmUuid ?? c?.nmUUID ?? ""
            );
            if (!id || existing.has(id)) continue;
            const name = String(c?.title || c?.vendorCode || `Товар ${id}`);
            const images: string[] = Array.isArray(c?.photos)
              ? c.photos
                  .map((ph: any) => ph?.big || ph?.c246x328 || ph?.square)
                  .filter(Boolean)
              : [];
            existing.set(id, {
              id,
              name,
              images,
            });
          }
          return Array.from(existing.values());
        });
        // Сохраняем курсор для следующей страницы (сериализуем в строку)
        const nextCursor = json?.cursor
          ? JSON.stringify(json.cursor)
          : undefined;
        lastIdRef.current = nextCursor;
        setHasMore(Boolean(nextCursor));
      } else if (store.platform === "yandex") {
        // Новый листинг офферов по магазину (raw offer-mappings)
        const url = new URL(getApiUrl("/api/products/by-store"));
        url.searchParams.set("store", store.id);
        url.searchParams.set("limit", "20");
        // Можно хранить pageToken в lastIdRef как общий механизм
        if (lastIdRef.current)
          url.searchParams.set("pageToken", lastIdRef.current);
        const resp = await fetch(url.toString(), { headers: authHeaders });
        if (!resp.ok)
          throw new Error("Не удалось получить список офферов Яндекс.Маркет");
        const json = await resp.json();
        const offers = (json?.items || []) as YandexOfferMappingItem[];
        setProducts((prev) => {
          const existing = new Map<string, OzonListItem>();
          for (const p of prev) existing.set(String(p.id), p);
          for (const o of offers) {
            const id = String(o?.offer?.offerId || "");
            if (!id || existing.has(id)) continue;
            existing.set(id, {
              id,
              name: o?.offer?.name || id,
              images: o?.offer?.pictures || [],
              price: o?.offer?.basicPrice?.value,
              currency: o?.offer?.basicPrice?.currencyId,
            });
          }
          return Array.from(existing.values());
        });
        const nextToken =
          (json?.paging?.nextPageToken as string | undefined) || undefined;
        lastIdRef.current = nextToken;
        setHasMore(Boolean(nextToken));
      }
    } catch (e: any) {
      setError(e?.message || "Ошибка загрузки товаров");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, store]);

  useEffect(() => {
    loadStore();
  }, [loadStore]);

  useEffect(() => {
    if (!store) return;
    const hasServerFilter =
      (store.platform === "wildberries" && appliedSearchId.trim() !== "") ||
      (store.platform === "ozon" &&
        (appliedOzonOfferId.trim() !== "" ||
          appliedOzonProductId.trim() !== "")) ||
      (store.platform === "yandex" && appliedYandexOfferId.trim() !== "");

    const run = async () => {
      setProducts([]);
      setHasMore(false);
      lastIdRef.current = undefined;
      if (!hasServerFilter) {
        await loadProducts();
        return;
      }

      setLoading(true);
      setError(null);
      try {
        if (store.platform === "wildberries" && appliedSearchId) {
          const resp = await fetch(
            `${getApiUrl("/api/products")}/${encodeURIComponent(
              appliedSearchId
            )}?store=${encodeURIComponent(store.id)}`,
            { headers: authHeaders }
          );
          const json = await resp.json();
          if (!resp.ok || !json || json.message) {
            setProducts([]);
          } else {
            const images: string[] = Array.isArray(json?.photos)
              ? json.photos
                  .map((ph: any) => ph?.big || ph?.c246x328 || ph?.square)
                  .filter(Boolean)
              : [];
            const item = {
              id: String(json?.nmID ?? json?.nmId ?? json?.nmUUID ?? ""),
              name: String(json?.title || json?.vendorCode || "Товар"),
              images,
            } as OzonListItem;
            setProducts(item.id ? [item] : []);
          }
          setHasMore(false);
          return;
        }

        if (store.platform === "ozon") {
          const byOffer = appliedOzonOfferId.trim() !== "";
          const idValue = byOffer
            ? appliedOzonOfferId.trim()
            : appliedOzonProductId.trim();
          const url = `${getApiUrl("/api/universal-products/info")}/${encodeURIComponent(
            idValue
          )}?platform=ozon&idType=${byOffer ? "offer" : "id"}`;
          const resp = await fetch(url, { headers: authHeaders });
          const json = await resp.json();
          if (!resp.ok || !json || json.message) {
            setProducts([]);
          } else {
            const imgs: string[] =
              Array.isArray(json?.images) && json.images.length
                ? json.images
                : Array.isArray(json?.primary_image)
                  ? json.primary_image
                  : [];
            const priceNum =
              json?.price != null ? Number(json.price) : undefined;
            const item: OzonListItem = {
              id: String(json?.id ?? json?.product_id ?? json?.sku ?? ""),
              name: String(json?.name || "Товар"),
              images: imgs,
              price: Number.isFinite(priceNum) ? priceNum : undefined,
              currency: json?.currency_code,
              offer_id: json?.offer_id,
              product_id: String(json?.id ?? ""),
            };
            setProducts(item.id ? [item] : []);
          }
          setHasMore(false);
          return;
        }

        if (store.platform === "yandex" && appliedYandexOfferId) {
          const resp = await fetch(
            `${getApiUrl("/api/products/by-store/mappings")}?store=${encodeURIComponent(
              store.id
            )}`,
            {
              method: "POST",
              headers: authHeaders,
              body: JSON.stringify({ offerIds: [appliedYandexOfferId.trim()] }),
            }
          );
          const json = await resp.json();
          const first = Array.isArray(json?.items) ? json.items[0] : null;
          const offer = first?.offer;
          if (!resp.ok || !offer) {
            setProducts([]);
          } else {
            const item: OzonListItem = {
              id: String(offer.offerId || ""),
              name: String(offer.name || offer.offerId || "Товар"),
              images: Array.isArray(offer.pictures) ? offer.pictures : [],
              price:
                offer?.basicPrice?.value != null
                  ? Number(offer.basicPrice.value)
                  : undefined,
            };
            setProducts(item.id ? [item] : []);
          }
          setHasMore(false);
          return;
        }
      } catch (e: any) {
        setError(e?.message || "Ошибка загрузки товара");
        setProducts([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [
    store,
    loadProducts,
    authHeaders,
    appliedSearchId,
    appliedOzonOfferId,
    appliedOzonProductId,
    appliedYandexOfferId,
  ]);

  const openProduct = (productId: string) => {
    if (!store) return;
    // У нас уже есть детальная страница: /products/:storeId/:productId
    navigate(`/products/${store.id}/${encodeURIComponent(productId)}`);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50 p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-800">Поиск товаров</div>
            <div className="text-xs text-gray-500 mt-0.5">Введите идентификатор и нажмите Найти</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
          {store?.platform === "wildberries" ? (
            <>
              <input
                type="text"
                placeholder="nmID товара"
                className="w-48 rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm outline-none transition focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchProductId}
                onChange={(e) => setSearchProductId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const v = searchProductId.trim();
                    setAppliedSearchId(v);
                  }
                }}
              />
              <button
                type="button"
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm ${
                  searchProductId.trim() !== ""
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-600 opacity-60 cursor-not-allowed"
                }`}
                disabled={searchProductId.trim() === ""}
                onClick={() => {
                  const v = searchProductId.trim();
                  setAppliedSearchId(v);
                }}
                title="Найти по nmID"
              >
                Найти
              </button>
              {appliedSearchId ? (
                <button
                  type="button"
                  className="rounded-lg px-3 py-2 text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setAppliedSearchId("");
                    setSearchProductId("");
                  }}
                  title="Сбросить фильтр"
                >
                  Сбросить
                </button>
              ) : null}
            </>
          ) : store?.platform === "ozon" ? (
            <>
              <input
                type="text"
                placeholder="offer_id"
                className="w-40 rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm outline-none transition focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={ozonOfferId}
                onChange={(e) => setOzonOfferId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && ozonOfferId.trim()) {
                    const v = ozonOfferId.trim();
                    setAppliedOzonOfferId(v);
                    setAppliedOzonProductId("");
                    setOzonProductId("");
                  }
                }}
              />
              <button
                type="button"
                className={`rounded-lg px-3 py-2 text-sm font-medium text-white shadow-sm ${
                  ozonOfferId.trim()
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-600 opacity-60 cursor-not-allowed"
                }`}
                disabled={!ozonOfferId.trim()}
                onClick={() => {
                  const v = ozonOfferId.trim();
                  if (!v) return;
                  setAppliedOzonOfferId(v);
                  setAppliedOzonProductId("");
                  setOzonProductId("");
                }}
                title="Найти по offer_id"
              >
                Найти
              </button>
              <input
                type="text"
                placeholder="product_id"
                className="w-40 rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm outline-none transition focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={ozonProductId}
                onChange={(e) => setOzonProductId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && ozonProductId.trim()) {
                    const v = ozonProductId.trim();
                    setAppliedOzonProductId(v);
                    setAppliedOzonOfferId("");
                    setOzonOfferId("");
                  }
                }}
              />
              <button
                type="button"
                className={`rounded-lg px-3 py-2 text-sm font-medium text-white shadow-sm ${
                  ozonProductId.trim()
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-600 opacity-60 cursor-not-allowed"
                }`}
                disabled={!ozonProductId.trim()}
                onClick={() => {
                  const v = ozonProductId.trim();
                  if (!v) return;
                  setAppliedOzonProductId(v);
                  setAppliedOzonOfferId("");
                  setOzonOfferId("");
                }}
                title="Найти по product_id"
              >
                Найти
              </button>
              {appliedOzonOfferId || appliedOzonProductId ? (
                <button
                  type="button"
                  className="rounded-lg px-3 py-2 text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setAppliedOzonOfferId("");
                    setAppliedOzonProductId("");
                    setOzonOfferId("");
                    setOzonProductId("");
                  }}
                  title="Сбросить фильтры"
                >
                  Сбросить
                </button>
              ) : null}
            </>
          ) : store?.platform === "yandex" ? (
            <>
              <input
                type="text"
                placeholder="offerId"
                className="w-48 rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm outline-none transition focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={yandexOfferId}
                onChange={(e) => setYandexOfferId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && yandexOfferId.trim()) {
                    const v = yandexOfferId.trim();
                    setAppliedYandexOfferId(v);
                  }
                }}
              />
              <button
                type="button"
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm ${
                  yandexOfferId.trim()
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-600 opacity-60 cursor-not-allowed"
                }`}
                disabled={!yandexOfferId.trim()}
                onClick={() => {
                  const v = yandexOfferId.trim();
                  if (!v) return;
                  setAppliedYandexOfferId(v);
                }}
                title="Найти по offerId"
              >
                Найти
              </button>
              {appliedYandexOfferId ? (
                <button
                  type="button"
                  className="rounded-lg px-3 py-2 text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setAppliedYandexOfferId("");
                    setYandexOfferId("");
                  }}
                  title="Сбросить фильтр"
                >
                  Сбросить
                </button>
              ) : null}
            </>
          ) : null}
          </div>
        </div>
      </div>
      {error ? <div className="text-red-600">{error}</div> : null}
      {!store && !error ? <div>Загрузка...</div> : null}
      {store && (
        <>
          {products.length === 0 && loading ? (
            <div className="flex items-center justify-center py-12 text-gray-600">
              Загрузка товаров…
            </div>
          ) : null}
          {products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((p) => (
                <button
                  key={`${p.id}`}
                  className="text-left bg-white rounded-2xl shadow-md border border-gray-200 hover:shadow-lg hover:border-gray-300 transition group overflow-hidden"
                  onClick={() => openProduct(p.id)}
                >
                  {p.images && p.images[0] ? (
                    <img
                      src={p.images[0]}
                      alt={p.name}
                      className="w-full h-44 object-cover"
                    />
                  ) : (
                    <div className="w-full h-44 bg-gray-100" />
                  )}
                  <div className="p-4">
                    <div className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                      {p.name}
                    </div>
                    {p.price != null ? (
                      <div className="text-sm text-gray-700 mt-1">
                        {p.price} {p.currency || "RUB"}
                      </div>
                    ) : null}
                    <div className="text-xs text-gray-500 mt-1">
                      {store?.platform === "wildberries" ? "nmID" : "ID"}: {" "}
                      {p.id}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : null}
          {hasMore ? (
            <div className="flex justify-center">
              <button
                className="mt-4 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-full hover:bg-blue-700 shadow-sm"
                onClick={() => loadProducts()}
                disabled={loading}
              >
                {loading ? "Загрузка…" : "Загрузить ещё"}
              </button>
            </div>
          ) : null}
          {!loading && products.length === 0 && !error ? (
            <div className="text-center py-16">
              <div className="inline-flex flex-col items-center space-y-3 p-8 bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl border border-gray-200 shadow-sm">
                <div className="p-3 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full">
                  <svg className="w-7 h-7 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 9m5-9v9m4-9v9m4-9l2 9" />
                  </svg>
                </div>
                <div className="text-gray-700 font-semibold">Товары не найдены</div>
                <div className="text-sm text-gray-500">Измените параметры поиска</div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default StoreProducts;
